import { createTheme } from "@mui/material/styles";
import {
  PaletteOptions,
  TypographyVariantsOptions,
} from "@mui/material/styles";

// Define TypeScript interfaces for your custom theme
interface CustomPaletteOptions extends PaletteOptions {
  neutral: {
    dark: string;
    main: string;
    light: string;
  };
}

interface CustomTypographyOptions extends TypographyVariantsOptions {
  body3: React.CSSProperties;
}

// Define type for shades
interface Shades {
  [key: string]: {
    [key: number]: string;
  };
}

export const shades: Shades = {
  primary: {
    100: "#ccd2d9",
    200: "#99a5b2",
    300: "#66798c",
    400: "#334c65",
    500: "#001f3f",
    600: "#001932",
    700: "#001326",
    800: "#000c19",
    900: "#00060d",
  },
  secondary: {
    100: "#ccf0f0",
    200: "#99e1e1",
    300: "#66d1d1",
    400: "#33c2c2",
    500: "#00b3b3",
    600: "#008f8f",
    700: "#006b6b",
    800: "#004848",
    900: "#002424",
  },
  neutral: {
    100: "#fdfdfd",
    200: "#fbfbfb",
    300: "#f9f9f9",
    400: "#f7f7f7",
    500: "#f5f5f5",
    600: "#c4c4c4",
    700: "#939393",
    800: "#626262",
    900: "#313131",
  },
};

// Extend the Theme interface
declare module "@mui/material/styles" {
  interface Palette {
    neutral: {
      dark: string;
      main: string;
      light: string;
    };
  }
  interface PaletteOptions {
    neutral: {
      dark: string;
      main: string;
      light: string;
    };
  }
  interface TypographyVariants {
    body3: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    body3?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    body3: true;
  }
}

export const theme = createTheme({
  palette: {
    primary: {
      light: shades.primary[200],
      main: shades.primary[500],
      dark: "#0039CB",
    },
    secondary: {
      main: shades.secondary[500],
      light: "#61F4FF",
      dark: "#0092CC",
    },
    neutral: {
      dark: shades.neutral[700],
      main: shades.neutral[500],
      light: shades.neutral[100],
    },
    background: {
      default: shades.neutral[500],
      paper: "#FFFFFF",
    },
    text: {
      primary: "#333333",
      secondary: shades.secondary[500],
    },
    action: {
      active: "#FF851B",
      hover: "#FF851B",
    },
    success: {
      main: "#01FF70",
    },
  } as CustomPaletteOptions,
  typography: {
    fontFamily: ["Roboto Condensed", "sans-serif"].join(","),
    fontSize: 11,
    h1: {
      fontFamily: ["Poppins", "sans-serif"].join(","),
      fontSize: 48,
      fontWeight: "800",
      color: shades.secondary[500],
      "@media (max-width:600px)": {
        fontSize: 32,
      },
      "@media (max-width:0px)": {
        fontSize: 28,
      },
    },
    h2: {
      fontFamily: ["Poppins", "sans-serif"].join(","),
      fontSize: 36,
      fontWeight: "700",
      "@media (max-width:600px)": {
        fontSize: 28,
      },
      "@media (max-width:0px)": {
        fontSize: 24,
      },
    },
    h3: {
      fontFamily: ["Poppins", "sans-serif"].join(","),
      fontSize: 20,
      fontWeight: "600",
      "@media (max-width:600px)": {
        fontSize: 18,
      },
      "@media (max-width:0px)": {
        fontSize: 16,
      },
    },
    h4: {
      fontFamily: ["Poppins", "sans-serif"].join(","),
      fontSize: 16,
      fontWeight: "500",
      "@media (max-width:600px)": {
        fontSize: 14,
      },
      "@media (max-width:0px)": {
        fontSize: 12,
      },
    },
    h5: {
      fontFamily: ["Nunito Sans", "sans-serif"].join(","),
    },
    h6: {
      fontFamily: ["Nunito Sans", "serif"].join(","),
    },
    body1: {
      fontFamily: ["Roboto Condensed", "sans-serif"].join(", "),
      fontSize: 18,
    },
    body2: {
      fontFamily: ["Nunito Sans", "serif"].join(","),
      fontSize: 22,
      fontWeight: "bold",
      color: "#FF851B",
    },
    body3: {
      fontFamily: ["Nunito Sans", "serif"].join(","),
      fontSize: 17,
    },
  } as CustomTypographyOptions,
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
});
